import {createContext, use} from 'react'

import type {CellAddress, ItemId} from '../types'
import type {BoardSelectedCard, BoardSelectionIntent} from './types'

/**
 * Selection is published as one context, and that context never changes.
 *
 * It is its own rather than part of the config or the data, because it changes on a
 * gesture and those do not: a card that reads a label, a count or a paging callback has
 * no business re-rendering because somebody selected a different card.
 *
 * Within it, the same shape the focus store uses, and for the same measured reason.
 * Every card on the board needs one fact -- whether it is the selected one -- and a
 * context value carrying the selection itself would invalidate all of them on every
 * gesture, so the cost of a click would grow with what is on screen rather than with
 * what changed. `focus/contexts.ts` records what that cost was when it was measured. So
 * the selection lives behind a store that cards subscribe to through
 * `useSyncExternalStore`, each for its own boolean, and a gesture wakes only the cards
 * whose answer changed.
 *
 * The store holds no selected ids. It reads the ones the caller stated, and reports what
 * a gesture would make of them.
 */
export type BoardSelectionStore = {
  /**
   * Whether the caller states this card as selected. Read without subscribing, for a
   * card that already knows it has to re-render.
   */
  isSelected: (card: BoardSelectedCard) => boolean
  /** The whole of what the caller states is selected, as the array the caller stated. */
  getSelectedCards: () => readonly BoardSelectedCard[]
  subscribe: (listener: () => void) => () => void
  /**
   * Works out what this gesture makes of the caller's selection and hands the result
   * back to the caller. Nothing is applied here, because nothing is held here.
   *
   * Does nothing at all on a board whose caller supplied no `onSelectionChange`: such a
   * board has no selection to change, and a gesture with nowhere to report to is not one.
   */
  selectCard: (card: BoardSelectedCard, intent: BoardSelectionIntent) => void
  /**
   * Hands the caller a selection outright, for the things a gesture on a card cannot say:
   * clearing, and selecting a whole cell. Reported the same way, and reported only when
   * it differs from what the caller already stated.
   */
  selectCards: (cards: readonly BoardSelectedCard[]) => void
  /** One cell's loaded cards, in the order the caller stated them. */
  cellItemIds: (address: CellAddress) => readonly ItemId[]
}

export const BoardSelectionContext = createContext<BoardSelectionStore | undefined>(undefined)

export function useBoardSelectionStore(): BoardSelectionStore {
  const value = use(BoardSelectionContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}
